import {
    Alert,
    Button,
    Col,
    DatePicker,
    Divider,
    Form,
    Input,
    InputNumber,
    message,
    Popconfirm,
    Row,
    Typography
} from "antd";
import {FormDataValues, FormItemClassNames, formItemLayout, getLabelInValue, tailFormItemLayout} from "common/form";
import {LocationSelect} from "components/form/select/LocationSelect";
import {Link, useLocation} from "react-router-dom";
import React from "react";
import {momentOrNull} from "common/util";
import moment from "moment";
import { useSubmit } from "./actions";
import { useRemoveFromFlight } from "../actions";
import DateTimePicker, {DateTimePickerValue} from "components/date-time-picker";
import {FormData, FormProps, PaxNodeFields} from "./types";
import {WrappedFormUtils} from "antd/lib/form/Form";
import useUserGroups from "../../../hooks/useUserGroups";
import {hasEditPermission} from "../util";

export interface CheckinDetailsProps {
    editingEnabled: boolean,
    initialData: PaxNodeFields,
    formData?: FormData,
    onFormDataChanged?: (newData: FormData) => void,
    onFormValuesChanged?: (newValues: FormDataValues<FormData>) => void,
    onPassengerUpdated?: (paxNode: PaxNodeFields) => void,
    onSubmitStatusUpdated?: (isSubmitting: boolean) => void,
    onRemoveFromFlight?: () => void,
    isRemovingFromFlight?: boolean
}

interface InternalFormProps extends FormProps {
    isUpdating: boolean,
    onFormSubmit: (form: WrappedFormUtils<PaxNodeFields>) => void,
    userGroups: ReturnType<typeof useUserGroups>[0],
    routerLocation: any
}

class CheckinDetails_Internal extends React.Component<CheckinDetailsProps & InternalFormProps>{

    isEditing = () => this.props.editingEnabled

    getCheckedInTime = () => {
        const cit = momentOrNull(this.props.initialData.checkedInTime);

        if (cit?.isSameOrBefore(moment('1970-01-01:00:00Z'))){
            return null;
        }

        return cit;
    }

    handleFormSubmit = () => {
        if (typeof this.props.onFormSubmit == 'function'){
            this.props.onFormSubmit(this.props.form);
        }
    }

    onRemoveFromFlight = () => {
        if (typeof this.props.onRemoveFromFlight === 'function'){
            this.props.onRemoveFromFlight();
        }
    }

    isScheduling = () => (this.props.routerLocation.pathname.startsWith('/app/scheduling'))

    render() {
        const paxNode = this.props.initialData;

        if (!paxNode) return null;

        const isOnFlight = !!paxNode.currentCarrierDoc;

        const {form: {getFieldDecorator, getFieldValue}} = this.props;

        let onFlightAlert = null;

        let hasPermission = (
            hasEditPermission(this.props.routerLocation.pathname, this.props.userGroups)
        )

        const editingDisabled = !this.isEditing() || !hasPermission;

        if (paxNode.currentCarrierDoc && !editingDisabled) {

            const flightUrl = `/app/flights/${paxNode.currentCarrierDoc._id}`;

            onFlightAlert = (
                <Alert
                    type="warning"
                    showIcon
                    message="Departure/destination disabled. Passenger is assigned to a flight."
                    description={
                        <span>
                        <Link className="ant-anchor-link" to={flightUrl}>
                            Edit flight '{paxNode.currentCarrierDoc.desig}'
                        </Link>
                    </span>
                    }
                    style={{
                        marginBottom: 12
                    }}
                />
            )
        }

        let flightInput = (
            <Input
                disabled={true}
                value={this.props.initialData.currentCarrierDoc?.desig}
                placeholder="No flight assigned"
                style={{ flex: 1 }}
            />
        )

        if (this.isEditing() && this.props.initialData.currentCarrierDoc && hasPermission){

            const title = <>
                <span>Are you sure you want to remove this pax from the flight?</span><br/>
                <span>This pax will be removed from all flight legs.</span>
            </>

            flightInput = (
                <Input.Group compact style={{ display: 'flex' }}>
                    {flightInput}
                    <Popconfirm
                        title={title}
                        onConfirm={this.onRemoveFromFlight}
                        okText="Remove"
                        okButtonProps={{ type: 'danger' }}
                    >
                        <Button loading={this.props.isRemovingFromFlight}>Remove</Button>
                    </Popconfirm>
                </Input.Group>
            )
        }

        let readOnlyAlert = null;
        if (editingDisabled){
            readOnlyAlert = (
                <Alert
                    type="warning"
                    showIcon
                    message="Read-only mode"
                    description="You are not authorized to make changes"
                    style={{
                        marginBottom: 12
                    }}
                />
            )
        }

        let showCheckedInTime = true;
        if (this.isScheduling()){
            showCheckedInTime = false;
        }


        return <Form style={{ position: 'relative' }}>
            {readOnlyAlert}
            <Form.Item className={FormItemClassNames.NoMargin} label="Current Flight" {...formItemLayout}>
                {flightInput}
            </Form.Item>
            <Divider type="horizontal" style={{ margin: '6px 0' }} />
            <Form.Item className={FormItemClassNames.NoMargin} label="Scheduled Flight Date" {...formItemLayout}>
                {getFieldDecorator('scheduledFlightDate', {
                    initialValue: momentOrNull(this.props.initialData.scheduledFlightDate),
                    rules: [{ required: true, message: 'Scheduled Flight Date is required' }]
                })(
                    <DatePicker disabled={this.isScheduling() || editingDisabled} />
                )}
            </Form.Item>
            {showCheckedInTime ? (
                <Form.Item className={FormItemClassNames.NoMargin} label="Checked In Time" {...formItemLayout}>
                    {getFieldDecorator('checkedInTime', {
                        initialValue: DateTimePickerValue.from(this.getCheckedInTime()),
                        rules: [ { required: false, validator: DateTimePickerValue.validate } ]
                    })(
                        <DateTimePicker disabled={editingDisabled} datePickerProps={{ format: 'YYYY-MM-DD' }} />
                    )}
                    <span style={{ marginLeft: '1rem' }}>
                    <Typography.Text type="secondary">
                        {DateTimePickerValue.toMoment(getFieldValue('checkedInTime'))?.utc().format('YYYY-MM-DD HH:mm')}
                        <span> UTC</span>
                    </Typography.Text>
                </span>
                </Form.Item>
            ) : null}
            <Form.Item className={FormItemClassNames.NoMargin} label="Controlling Heliport" {...formItemLayout}>
                {getFieldDecorator('lastKnownController', {
                    initialValue: getLabelInValue(this.props.initialData.lastKnownController, 'name'),
                    rules: [{ required: true, message: 'Controlling Heliport is required' }]
                })(
                    <LocationSelect disabled={this.isScheduling() || editingDisabled} hasDataflytHardware labelInValue/>
                )}
            </Form.Item>
            <Divider type="horizontal" style={{ margin: '6px 0' }} />
            {onFlightAlert}
            <Form.Item className={FormItemClassNames.NoMargin} label="Departure" {...formItemLayout}>
                {getFieldDecorator('departureID', {
                    initialValue: getLabelInValue(this.props.initialData.departureID, 'name'),
                })(
                    <LocationSelect disabled={editingDisabled || isOnFlight} labelInValue/>
                )}
            </Form.Item>
            <Form.Item className={FormItemClassNames.NoMargin} label="Destination" {...formItemLayout}>
                {getFieldDecorator('destinationID', {
                    initialValue: getLabelInValue(this.props.initialData.destinationID, 'name')
                })(
                    <LocationSelect disabled={editingDisabled || isOnFlight} labelInValue/>
                )}
            </Form.Item>
            <Divider type="horizontal" style={{ margin: '6px 0' }} />
            <Form.Item className={FormItemClassNames.NoMargin} label="Charge Code" {...formItemLayout}>
                {getFieldDecorator('chargeCode', {
                    initialValue: this.props.initialData.chargeCode
                })(
                    <Input disabled={editingDisabled} />
                )}
            </Form.Item>
            <Divider type="horizontal" style={{ margin: '6px 0' }} />
            <Row>
                <Col {...formItemLayout.labelCol} />
                <Col {...formItemLayout.wrapperCol}>
                    <Row>
                        <Col sm={6}>
                            <Form.Item label="PAX Weight">
                                {getFieldDecorator('paxWeight', {
                                    initialValue: this.props.initialData.paxWeight
                                })(
                                    <InputNumber disabled={editingDisabled} min={0} />
                                )}
                            </Form.Item>
                        </Col>
                        <Col sm={6}>
                            <Form.Item label="Bag Weight">
                                {getFieldDecorator('bagWeight', {
                                    initialValue: this.props.initialData.bagWeight
                                })(
                                    <InputNumber disabled={editingDisabled} min={0} />
                                )}
                            </Form.Item>
                        </Col>
                        <Col md={6}>
                            <Form.Item label="Bag Count">
                                {getFieldDecorator('bagCount', {
                                    initialValue: this.props.initialData.bagCount
                                })(
                                    <InputNumber disabled={editingDisabled} min={0} />
                                )}
                            </Form.Item>
                        </Col>
                    </Row>
                </Col>
            </Row>
            {!editingDisabled ? (
                <Form.Item className={FormItemClassNames.NoMargin} label="" {...tailFormItemLayout}>
                    <Button
                        type="primary"
                        onClick={this.handleFormSubmit}
                        loading={this.props.isUpdating}
                        disabled={!this.props.form.isFieldsTouched()}
                        size="large"
                    >Submit</Button>
                    {this.props.form.isFieldsTouched() ? (
                        <Button
                            type="link"
                            onClick={() => this.props.form.resetFields()}
                        >Reset</Button>
                    ) : null}
                </Form.Item>
            ) : null}
        </Form>
    }
}

const CheckInDetails_Form = Form.create<CheckinDetailsProps & InternalFormProps>({
    mapPropsToFields(props){
        if (!props.editingEnabled) return {};
        return props.formData
    },
    onFieldsChange(props, _, allFields){
        if (typeof props.onFormDataChanged === 'function'){
            props.onFormDataChanged(allFields);
        }
    },
    onValuesChange(props, values){
        if (typeof props.onFormValuesChanged === 'function'){
            props.onFormValuesChanged(values);
        }
    }
})(CheckinDetails_Internal)

function FlightCheckInDetails(props: CheckinDetailsProps) {
    const [ userGroups ] = useUserGroups();
    const routerLocation = useLocation();
    const { sendUpdate, isProcessing } = useSubmit(props.initialData);
    const { remove, loading: removing } = useRemoveFromFlight(props.initialData);

    function onPaxUpdated(paxNode: PaxNodeFields){
        if (typeof props.onPassengerUpdated === 'function'){
            props.onPassengerUpdated(paxNode);
        }
    }

    function handleRemoveFromFlight(){
        remove()
            .then((result) => {
                message.success('Successfully removed pax from flight');
                const paxNodes = result.data.removePaxCgoFromFlight.paxNodes;
                if (Array.isArray(paxNodes) && paxNodes.length > 0){
                    onPaxUpdated(paxNodes[0]);
                }
            })
            .catch((err) => {
                console.log(err);
                message.error('Failed to remove pax from flight. Check console for details.');
            })
    }

    function handleSubmit(form: WrappedFormUtils<PaxNodeFields>){

        const originalFormData = {...props.formData};

        form.validateFieldsAndScroll(async (err: any, values: FormDataValues<FormData>) => {
            if (!err){
                try {
                    const newPaxNode = await sendUpdate({
                        _id: props.initialData._id,
                        tpID: props.initialData.tpID._id,
                        checkedInTime: values.checkedInTime ? DateTimePickerValue.toMoment(values.checkedInTime)?.toISOString() : null,
                        scheduledFlightDate: values.scheduledFlightDate?.format('YYYY-MM-DD'),
                        lastKnownController: values.lastKnownController.key,
                        departureID: values.departureID.key,
                        destinationID: values.destinationID.key,
                        paxWeight: values.paxWeight,
                        bagWeight: values.bagWeight,
                        bagCount: values.bagCount,
                        chargeCode: values.chargeCode
                    })
                    message.success('Successfully updated passenger check-in details.');
                    onPaxUpdated(newPaxNode);

                    form.resetFields();
                }
                catch(err){
                    message.error("Failed to update passenger check-in details.");
                    console.error(err);
                    if (typeof props.onFormDataChanged === 'function'){
                        // Reset back original form data if it fails to upload
                        props.onFormDataChanged(originalFormData);
                        return;
                    }
                }
            }
        });
    }

        return <CheckInDetails_Form
            {...props}
            isUpdating={isProcessing}
            onFormDataChanged={props.onFormDataChanged}
            onFormSubmit={handleSubmit}
            onRemoveFromFlight={handleRemoveFromFlight}
            isRemovingFromFlight={removing}
            userGroups={userGroups}
            routerLocation={routerLocation}
        />
    }

export default FlightCheckInDetails;